/**
 * MemberContextBand · the "continuity spine" one-liner.
 *
 * A soft italic serif line that appears above a pillar's hero when the
 * Concierge has *noticed* something from an adjacent pillar. Never a CTA,
 * never a supplier name, never a price. Just a quiet whisper of memory.
 *
 * Usage:
 *     <MemberContextBand pillar="table" />
 *
 * The component silently renders nothing when:
 *   · the caller isn't authenticated
 *   · cross-pillar consent is off
 *   · the member is in their quiet hours
 *   · chattiness has dialled to "silent" after repeated dismisses
 *   · the pillar is `dreamweaver` (silo enforced server + client)
 *
 * When shown, `noted` fades the band and remembers the choice server-side.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  brass:  "#a97e2d",
  brassSoft: "#c9a663",
  ink:    "#1a1a1a",
  muted:  "rgba(26,26,26,0.55)",
  border: "rgba(26,26,26,0.10)",
  cream:  "#fbf9f4",
};

// NOTE · Dreamweaver signals are siloed *server-side* (record_signal
// never persists them). But the /dreamweaver surface CAN render a
// band composed from the member's OTHER pillars — so we no longer
// short-circuit here on pillar==="dreamweaver". The backend's line
// template for dreamweaver is deliberately soft ("we remember you —
// whatever you're about to whisper, we hold it gently") and never
// references the dream itself.

export default function MemberContextBand({ pillar, style }) {
  const [band, setBand]         = useState(null);
  const [dismissed, setDism]    = useState(false);
  const [dismissing, setDsg]    = useState(false);

  useEffect(() => {
    if (!pillar) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/member-context/band`,
          { params: { pillar }, withCredentials: true }
        );
        if (alive && r.data && r.data.band) setBand(r.data.band);
      } catch (_) {
        // Silent — no band is a valid state.
      }
    })();
    return () => { alive = false; };
  }, [pillar]);

  async function onDismiss() {
    setDsg(true);
    try {
      await axios.post(
        `${API}/api/member-context/band/dismiss`,
        { pillar, band_id: band?.band_id },
        { withCredentials: true }
      );
    } catch (_) { /* silent */ }
    setDism(true);
    // Fade over 400ms, then unmount cleanly.
    setTimeout(() => setBand(null), 420);
  }

  if (!band || dismissed && !band) return null;

  return (
    <div
      data-testid={`context-band-${pillar}`}
      role="status"
      aria-live="polite"
      style={{
        maxWidth: 780,
        margin: "0 auto 22px",
        padding: "12px 18px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        borderLeft: `2px solid ${T.brassSoft}`,
        background: "transparent",
        opacity: dismissed ? 0 : 1,
        transform: dismissed ? "translateY(-4px)" : "translateY(0)",
        transition: "opacity 400ms ease, transform 400ms ease",
        ...(style || {}),
      }}
    >
      <div
        data-testid={`context-band-line-${pillar}`}
        style={{
          flex: 1,
          fontFamily: '"EB Garamond", Georgia, serif',
          fontStyle: "italic",
          fontSize: 15,
          lineHeight: 1.55,
          color: T.muted,
          letterSpacing: "0.005em",
        }}
      >
        {band.line}
      </div>
      {/* Only personalized bands are dismissable · the essence line
          is the pillar's own voice, not a suggestion, so there's
          nothing to dismiss. Cleaner. */}
      {band.type === "personal" && (
        <button
          type="button"
          onClick={onDismiss}
          disabled={dismissing}
          data-testid={`context-band-noted-${pillar}`}
          title="A polite ‘I saw it, thank you’ — we'll speak more softly."
          style={{
            background: "transparent",
            border: 0,
            padding: "2px 6px",
            cursor: "pointer",
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: 13,
            fontStyle: "italic",
            color: T.brass,
            opacity: dismissing ? 0.5 : 1,
          }}
        >
          ✕ noted
        </button>
      )}
    </div>
  );
}
